(() => {
  const RECENT_SEARCHES_KEY = 'doria_recent_searches_v5';
  const MAX_RECENT_SEARCHES = 6;
  const PLACEHOLDER_ROTATE_DELAY = 1800;

  const rotationMap = new WeakMap();

  const isArabic = () =>
    (document.documentElement.lang || '').toLowerCase().startsWith('ar') ||
    document.documentElement.dir === 'rtl';

  const uiText = isArabic()
    ? {
        explore: 'استكشف',
        recent: 'آخر عمليات البحث',
      }
    : {
        explore: 'Explore',
        recent: 'Recent searches',
      };

  const basePhrases = isArabic()
    ? [
        'ابحث عن أحمد المغربي',
        'ابحث عن لطافة',
        'ابحث عن أرماف',
        'ابحث عن أفنان',
        'ابحث عن فرنش أفينيو',
        'ابحث عن عطور رجالية',
        'ابحث عن عطور نسائية',
        'ابحث عن عطور فيرومونية',
        'ابحث عن عطور صيفية',
        'ابحث عن عود',
        'ابحث عن بخور',
        'ابحث عن الأكثر مبيعًا',
      ]
    : [
        'Search for Ahmed Al Maghribi',
        'Search for Lattafa',
        'Search for Armaf',
        'Search for Afnan',
        'Search for French Avenue',
        'Search for Men Perfumes',
        'Search for Women Perfumes',
        'Search for Pheromone Perfumes',
        'Search for Summer Perfumes',
        'Search for Oud',
        'Search for Bakhoor',
        'Search for Best Seller Perfumes',
      ];

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const normalizeList = (items) => {
    const seen = new Set();

    return items
      .map((item) => String(item || '').trim())
      .filter((item) => item.length > 1 && item.length < 60)
      .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const getRecentSearches = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  };

  const saveRecentSearch = (query) => {
    const value = String(query || '').trim();
    if (!value) return;

    const recent = getRecentSearches().filter(
      (item) => item.toLowerCase() !== value.toLowerCase()
    );

    recent.unshift(value);

    window.localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES))
    );
  };

  const getContextTerms = () => {
    const selectors = [
      '.header__inline-menu .header__menu-item',
      '.brand-tabs-pill',
      '.brand-tabs-pills button',
      '.featured-collection .title',
      '.collection .title',
    ];

    const texts = selectors.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector)).map((el) => el.textContent.trim())
    );

    return normalizeList(texts).slice(0, 10);
  };

  const buildPhrase = (term) => {
    const value = String(term || '').trim();
    if (!value) return null;

    if (isArabic()) {
      if (value.startsWith('ابحث عن')) return value;
      return `ابحث عن ${value}`;
    }

    if (value.toLowerCase().startsWith('search for')) return value;
    return `Search for ${value}`;
  };

  const getRotatingPhrases = () => {
    const contextual = getContextTerms().map(buildPhrase);
    const recent = getRecentSearches().map(buildPhrase);

    return normalizeList([...recent, ...contextual, ...basePhrases]).slice(0, 12);
  };

  const getExploreTerms = () => {
    const defaults = isArabic()
      ? ['أحمد المغربي', 'لطافة', 'أرماف', 'أفنان', 'عود', 'بخور', 'عطور رجالية', 'عطور نسائية']
      : ['Ahmed Al Maghribi', 'Lattafa', 'Armaf', 'Afnan', 'Oud', 'Bakhoor', 'Men Perfumes', 'Women Perfumes'];

    return normalizeList([...getContextTerms(), ...defaults]).slice(0, 8);
  };

  const submitSearch = (form, input, query) => {
    const value = String(query || '').trim();
    if (!value) return;

    input.value = value;
    saveRecentSearch(value);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  };

  const stopPlaceholderRotation = (input) => {
    const existing = rotationMap.get(input);
    if (!existing) return;

    clearInterval(existing.intervalId);
    rotationMap.delete(input);

    if (!String(input.value || '').trim() && input.dataset.defaultPlaceholder) {
      input.setAttribute('placeholder', input.dataset.defaultPlaceholder);
    }
  };

  const startPlaceholderRotation = (input) => {
    if (!input) return;

    stopPlaceholderRotation(input);

    const phrases = getRotatingPhrases();
    if (!phrases.length) return;

    if (!input.dataset.defaultPlaceholder) {
      input.dataset.defaultPlaceholder =
        input.getAttribute('placeholder') || (isArabic() ? 'ابحث' : 'Search');
    }

    let index = 0;

    const applyPhrase = () => {
      if (!document.body.classList.contains('search-overlay-open')) return;
      if (String(input.value || '').trim()) return;

      input.setAttribute('placeholder', phrases[index]);
      index = (index + 1) % phrases.length;
    };

    applyPhrase();

    const intervalId = setInterval(applyPhrase, PLACEHOLDER_ROTATE_DELAY);
    rotationMap.set(input, { intervalId });
  };

  const renderSearchExtras = (dialog, form, input) => {
    let extras = dialog.querySelector('.search-modal__extras');

    if (!extras) {
      extras = document.createElement('div');
      extras.className = 'search-modal__extras';
      form.insertAdjacentElement('afterend', extras);
    }

    const exploreTerms = getExploreTerms();
    const recentQueries = getRecentSearches();

    const explorePanel = exploreTerms.length
      ? `
        <div class="search-modal__panel">
          <div class="search-modal__panel-title">${escapeHtml(uiText.explore)}</div>
          <div class="search-modal__chips">
            ${exploreTerms
              .map(
                (term) => `
                  <button type="button" class="search-modal__chip search-modal__chip--menu" data-search-chip="${escapeHtml(term)}">
                    ${escapeHtml(term)}
                  </button>
                `
              )
              .join('')}
          </div>
        </div>
      `
      : '';

    const recentPanel = recentQueries.length
      ? `
        <div class="search-modal__panel">
          <div class="search-modal__panel-title">${escapeHtml(uiText.recent)}</div>
          <div class="search-modal__chips">
            ${recentQueries
              .map(
                (term) => `
                  <button type="button" class="search-modal__chip search-modal__chip--recent" data-search-chip="${escapeHtml(term)}">
                    ${escapeHtml(term)}
                  </button>
                `
              )
              .join('')}
          </div>
        </div>
      `
      : '';

    extras.innerHTML = `${explorePanel}${recentPanel}`;

    extras.querySelectorAll('[data-search-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        submitSearch(form, input, chip.getAttribute('data-search-chip'));
      });
    });
  };

  const syncSearchBodyState = () => {
    const isOpen = Array.from(document.querySelectorAll('details-modal details')).some(
      (details) => details.hasAttribute('open') && details.querySelector('.search-modal')
    );

    document.body.classList.toggle('search-overlay-open', isOpen);
  };

  const initSingleSearchModal = (modalHost) => {
    if (modalHost.dataset.searchOverlayV5 === 'true') return;

    const details = modalHost.querySelector('details');
    const dialog = modalHost.querySelector('.search-modal');
    const form = dialog?.querySelector('form');
    const input = form?.querySelector('input[type="search"]');
    const summary = details?.querySelector('summary');

    if (!details || !dialog || !form || !input) return;

    modalHost.dataset.searchOverlayV5 = 'true';

    const syncState = () => {
      const open = details.hasAttribute('open');
      syncSearchBodyState();

      if (open) {
        renderSearchExtras(dialog, form, input);

        setTimeout(() => {
          input.focus();
          startPlaceholderRotation(input);
        }, 120);
      } else {
        stopPlaceholderRotation(input);
      }
    };

    const closeSearch = () => {
      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      stopPlaceholderRotation(input);
      syncSearchBodyState();
    };

    const observer = new MutationObserver(syncState);
    observer.observe(details, {
      attributes: true,
      attributeFilter: ['open'],
    });

    summary?.addEventListener('click', () => {
      setTimeout(syncState, 40);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeSearch();
      }
    });

    input.addEventListener('input', () => {
      if (String(input.value || '').trim()) {
        stopPlaceholderRotation(input);
        input.setAttribute('placeholder', '');
      } else if (details.hasAttribute('open')) {
        startPlaceholderRotation(input);
      }
    });

    form.addEventListener('submit', () => {
      saveRecentSearch(input.value);
    });

    syncState();
  };

  const initSearchOverlayV3 = () => {
    document.querySelectorAll('details-modal').forEach(initSingleSearchModal);
    syncSearchBodyState();
  };

  const initAdvancedUi = () => {
    const body = document.body;
    const sectionHeader = document.querySelector('.section-header');
    const progressBar = document.querySelector('.site-scroll-progress__bar');
    const backToTop = document.querySelector('.back-to-top');
    const siteLoader = document.querySelector('.site-loader');

    if (!window.__advancedUiInitialized) {
      window.__advancedUiInitialized = true;

      const setHeaderState = () => {
        if (!sectionHeader) return;

        const scrollTop = window.scrollY || window.pageYOffset || 0;
        const atTop = scrollTop <= 12;

        body.classList.toggle('header-state-top', atTop);
        body.classList.toggle('header-state-scrolled', !atTop);
      };

      const updateScrollProgress = () => {
        if (!progressBar) return;

        const scrollTop = window.scrollY || window.pageYOffset || 0;
        const docHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight
        );
        const winHeight = window.innerHeight;
        const scrollable = Math.max(docHeight - winHeight, 1);
        const percent = Math.min((scrollTop / scrollable) * 100, 100);

        progressBar.style.width = `${percent}%`;
      };

      const updateBackToTop = () => {
        if (!backToTop) return;

        const scrollTop = window.scrollY || window.pageYOffset || 0;
        backToTop.classList.toggle('is-visible', scrollTop > 180);
      };

      const onScroll = () => {
        setHeaderState();
        updateScrollProgress();
        updateBackToTop();
      };

      if (backToTop) {
        backToTop.addEventListener('click', () => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        });
      }

      if (siteLoader) {
        const hideLoader = () => {
          siteLoader.classList.add('is-hidden');
          setTimeout(() => {
            siteLoader.style.display = 'none';
          }, 400);
        };

        if (document.readyState === 'complete') {
          hideLoader();
        } else {
          window.addEventListener('load', hideLoader, { once: true });
        }
      }

      if (sectionHeader) {
        const iconLinks = sectionHeader.querySelectorAll('.header__icon');
        iconLinks.forEach((icon) => {
          icon.addEventListener('mousedown', () => {
            icon.style.transform = 'scale(0.98)';
          });

          const reset = () => {
            icon.style.transform = '';
          };

          icon.addEventListener('mouseup', reset);
          icon.addEventListener('mouseleave', reset);
          icon.addEventListener('touchend', reset, { passive: true });
        });
      }

      setHeaderState();
      updateScrollProgress();
      updateBackToTop();

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }

    initSearchOverlayV3();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAdvancedUi, { once: true });
  } else {
    initAdvancedUi();
  }

  document.addEventListener('shopify:section:load', initAdvancedUi);
  document.addEventListener('shopify:section:reorder', initAdvancedUi);
  document.addEventListener('shopify:section:select', initAdvancedUi);
})();